import { HttpClientModule } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationRef,
  Compiler,
  COMPILER_OPTIONS,
  CompilerFactory,
  NgModule,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { JitCompilerFactory } from '@angular/platform-browser-dynamic';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import {
  createInputTransfer,
  createNewHosts,
  removeNgStyles,
} from '@angularclass/hmr';
import { ClickOutsideModule, FooterModule } from '@ipreo/ngx-sprinkles';
import { getLoader } from '@ipreo/nmp-loader';
import {
  AuthService,
  ENVIRONMENT_CONFIG,
  FeaturesFetchAction,
  LogService,
  NorthstarModule,
  SettingsFetchAction,
  WebsocketGatewayAuthType,
  WebsocketGatewayService,
} from '@ipreo/northstar';
import { NorthstarAlertsModule } from '@ipreo/northstar-alerts';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import {
  Action,
  ActionReducer,
  MetaReducer,
  Store,
  StoreModule,
} from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { take } from 'rxjs/operators';

import { environment } from './../environments/environment';

import { AppComponent } from './app.component';
import { devModules } from './app.dev-modules';
import { routes } from './app.routes';
import './app.system-bootstrap';
import { DashboardComponent } from './dashboard/dashboard.component';
import { DashboardService } from './dashboard/dashboard.service';
import { NoContentComponent } from './no-content/no-content.component';
import { HeaderDropdownComponent } from './shared/header/components/header-dropdown.component';
import { HeaderInfoComponent } from './shared/header/components/header-info.component';
import { HeaderLinkComponent } from './shared/header/components/header-link.component';
import { HeaderLogoComponent } from './shared/header/components/header-logo/header-logo.component';
import { HeaderTabsComponent } from './shared/header/components/header-tabs/header-tabs.component';
import { ClassificationNoticeModalComponent } from './shared/classification-notice-modal/classification-notice-modal.component';
import { UserMenuComponent } from './shared/header/components/user-menu/user-menu.component';
import { HeaderComponent } from './shared/header/header.component';
import { UserSettingsComponent } from './shared/user-settings/user-settings.component';
import { reducers, State } from './state/reducer';
if (!Object.prototype.hasOwnProperty.call(window, 'PolarisFirstLoad')) {
  window['PolarisFirstLoad'] = true;
}

const CUPCAKE_MAJOR_VERSION: number = parseInt(
  window.sessionStorage.getItem('cupcake-version'),
  10
);

function initializeSplunkRUM() {
  // Use window object to persist across HMR reloads
  if (window['SplunkInitialized']) {
    console.log(
      'Splunk RUM already initialized (persists across HMR), skipping...'
    );
    return;
  }

  const isValidSplunkEnvironment = isValidSplunkEnv();
  if (isValidSplunkEnvironment) {
    try {
      // bracket access avoids ReferenceError if the Splunk script was blocked/never loaded
      const SplunkRum = window['SplunkRum'];
      const SplunkSessionRecorder = window['SplunkSessionRecorder'];
      SplunkRum?.init({
        realm: 'us0',
        rumAccessToken: environment.splunkRumToken,
        applicationName: 'sellside',
        deploymentEnvironment: environment.env,
        instrumentations: {
          websocket: true,
          socketio: true,
        },
      });
      if (environment.splunkSessionReplayEnabled) {
        console.log('Splunk RUM Session Replay Initialized successfully');
        SplunkSessionRecorder?.init({
          realm: 'us0',
          rumAccessToken: environment.splunkRumToken,
        });
      }

      window['SplunkInitialized'] = true;
      console.log('Splunk RUM initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Splunk RUM:', error);
    }
  } else {
    console.log(
      'Splunk RUM initialization skipped - not production environment'
    );
  }
}

function isValidSplunkEnv() {
  const clientEnv = window.sessionStorage.getItem('client_env');
  const env = environment.env;
  return (
    (env === 'staging' && (clientEnv === 'perf' || clientEnv === 'demo')) ||
    env === 'prod'
  );
}

async function loadUserInfo(
  store: Store<State>,
  websocketGatewayService: WebsocketGatewayService,
  auth: AuthService
): Promise<boolean> {
  // Always call here so that on the OIDC callback path clientEnv is read AFTER
  // history.pushState() has restored the original URL (including devServer param).
  setClientEnv();

  await getLoader().authenticate(auth.currentUser.access_token);

  store.dispatch(new FeaturesFetchAction());
  store.dispatch(new SettingsFetchAction());

  setUserAttributesForSplunk(auth);

  websocketGatewayService.error$.subscribe(
    (error) => {
      console.warn(error);
    },
    (error) => {
      console.error('errors$ error', error);
    }
  );

  websocketGatewayService.starting$.subscribe(
    () => {
      console.log('Websocket Gateway connection has been started');
    },
    (error) => {
      console.warn('Websocket Gateway connection failed to start!', error);
    }
  );

  websocketGatewayService.closing$.subscribe(
    () => {
      console.log('Websocket Gateway connection has been closed');
    },
    (error) => {
      console.warn('Websocket Gateway connection failed to be close!', error);
    }
  );

  websocketGatewayService.connect(
    WebsocketGatewayAuthType.JWT,
    auth.currentUser.access_token
  );

  auth.userLoadedEvent.subscribe((user) => {
    websocketGatewayService.connect(
      WebsocketGatewayAuthType.JWT,
      user.access_token
    );
  });

  const pendo = window['pendo'];
  if (pendo) {
    pendo.initialize({
      visitor: {
        id: auth.currentUser.profile.sub,
        email: auth.currentUser.profile.email,
        full_name: auth.currentUser.profile.name,
        environment: environment.env,
      },
    });
  }

  window['PolarisFirstLoad'] = false;

  return true;
}

const startingPath = window.location.pathname + window.location.search;

const appInitializer = {
  provide: APP_INITIALIZER,
  useFactory:
    (
      store: Store<State>,
      auth: AuthService,
      websocketGatewayService: WebsocketGatewayService,
      logService: LogService
    ) =>
    () => {
      if (window['PolarisFirstLoad']) {
        logService.start();
        setClientEnv();
        initializeSplunkRUM();

        return auth.getUser().then((loggedIn) => {
          if (loggedIn) {
            return loadUserInfo(store, websocketGatewayService, auth);
          } else {
            return handleLoginFlow(auth, store, websocketGatewayService);
          }
        });
      }
      initializeSplunkRUM();
      return true;
    },
  deps: [Store, AuthService, WebsocketGatewayService, LogService],
  multi: true,
};

function setUserAttributesForSplunk(auth: AuthService) {
  const isValidSplunkEnvironment = isValidSplunkEnv();
  const user = auth.currentUser.profile;
  // bracket access avoids ReferenceError if the Splunk script was blocked/never loaded
  const SplunkRum = window['SplunkRum'];
  if (user && isValidSplunkEnvironment && SplunkRum) {
    console.log('Initializing Splunk RUM User Context');

    try {
      SplunkRum.setGlobalAttributes({
        user_id: user.sub,
        user_name: user.name,
        user_email: user.email,
      });
    } catch (error) {
      console.error('Failed to set Splunk user attributes', error);
      // Do not send any data to Splunk
    }
  }
}

function handleLoginFlow(
  auth: AuthService,
  store: Store<State>,
  websocketGatewayService: WebsocketGatewayService
): unknown {
  const path = window.location.pathname;
  if (path.includes('/idp-sso')) {
    // Do not trigger login flow for idp-sso route
    return auth.handleIdpSSO().subscribe(
      (isLoggedIn) =>
        handleLoginForIDPSSO(auth, store, websocketGatewayService, isLoggedIn),
      () => {
        initSamLogin(auth, store, websocketGatewayService);
      }
    );
  }

  return initSamLogin(auth, store, websocketGatewayService);
}

function handleLoginForIDPSSO(
  auth: AuthService,
  store: Store<State>,
  websocketGatewayService: WebsocketGatewayService,
  isLoggedIn: boolean
) {
  if (isLoggedIn) {
    return loadUserInfo(store, websocketGatewayService, auth);
  } else {
    return initSamLogin(auth, store, websocketGatewayService);
  }
}

function initSamLogin(
  auth: AuthService,
  store: Store<State>,
  websocketGatewayService: WebsocketGatewayService
): unknown {
  // Always pass the full absolute URL (or undefined) to signinRedirectCallback.
  // Previously a relative path (pathname+search) was passed, which causes
  // oidc-client to fail parsing the callback on some versions, silently
  // returning an empty state — so state['startUrl'] is never restored and
  // the devServer/clearDevServers query params are lost before routing.start().
  const callbackUrl = startingPath.includes('?')
    ? window.location.href
    : undefined;
  return auth.endSigninMainWindow(callbackUrl).then((state) => {
    if (auth.loggedIn) {
      history.pushState(
        '',
        document.title,
        state['startUrl'] === undefined ? startingPath : state['startUrl']
      );
      return loadUserInfo(store, websocketGatewayService, auth);
    } else {
      console.log('STARTING PATH: ', startingPath);

      auth.startSigninMainWindow({
        startUrl: startingPath,
      });
      return new Promise((resolve) => {
        setTimeout(() => resolve(null), 5000);
      });
    }
  });
}

export function setClientEnv() {
  const clientEnv = window.sessionStorage.getItem('client_env');

  if (!clientEnv) {
    const searchParams: URLSearchParams = new URL(window.location.href)
      .searchParams;
    //accessing clienEnv from dev server url
    const devServer = searchParams.get('devServer');
    if (devServer) {
      const devServerUrl = new URL(devServer);
      if (
        devServerUrl &&
        (devServerUrl.hostname ===
          'packages.dev.muni.globalmarkets.ihsmarkit.com' ||
          devServerUrl.hostname === 'local.muni.ipreo.com')
      ) {
        const clientEnvJson = devServerUrl.pathname.split('/').slice(-1)[0];
        const clientEnv = clientEnvJson.split('-')[1].split('.')[0];
        window.sessionStorage.setItem('client_env', clientEnv);
        return;
      }
    }

    //accessing clienEnv from query string params
    let clientEnv: string = null;
    searchParams.forEach((value, key) => {
      if (key.toLowerCase() === 'clientenv') {
        //case-insensitive comparison of searchparams
        clientEnv = value;
      }
    });
    if (!clientEnv && environment.env === 'dev') {
      clientEnv = 'dev'; // required for muni apps to work in dev env
    }
    if (clientEnv) {
      window.sessionStorage.setItem('client_env', clientEnv);
      return;
    }
  }
}

export function envFactory() {
  return {
    northstar: {
      host: {
        api: environment.apiBaseUrl,
        //hub: environment.hubUrl, //TODO: To be removed in future, as legacy WS is deprecated and IHSM push is used instead
        ws: environment.wsUrl,
        registry: environment.registryUrl,
      },
      env: environment.env,
      cupcakeMajorVersion: CUPCAKE_MAJOR_VERSION,
    },
  };
}

export function createCompiler(compilerFactory: CompilerFactory) {
  return compilerFactory.createCompiler();
}
/* eslint-disable @typescript-eslint/no-explicit-any */
export function stateSetter(reducer: ActionReducer<any>): ActionReducer<any> {
  return function (state: any, action: any) {
    if (action.type === 'SET_ROOT_STATE') {
      return action.payload;
    }
    return reducer(state, action);
  };
}
/* eslint-enable @typescript-eslint/no-explicit-any */
export const stateReloader: MetaReducer<State, Action>[] = [stateSetter];

interface InternalStateType {
  [key: string]: unknown;
}

interface StoreType {
  state: InternalStateType;
  rootState: InternalStateType;
  restoreInputValues: () => void;
  disposeOldHosts: () => void;
}

@NgModule({
  declarations: [
    AppComponent,
    DashboardComponent,
    NoContentComponent,
    HeaderComponent,
    HeaderLinkComponent,
    HeaderDropdownComponent,
    HeaderInfoComponent,
    HeaderLogoComponent,
    UserMenuComponent,
    HeaderTabsComponent,
    ClassificationNoticeModalComponent,
    UserSettingsComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    RouterModule.forRoot(routes, {
      enableTracing: environment.enableRouterTracing,
      initialNavigation: 'disabled',
    }),
    StoreModule.forRoot(reducers, { metaReducers: stateReloader }),
    ClickOutsideModule,
    environment.instrumentStore
      ? StoreDevtoolsModule.instrument({ maxAge: 200 })
      : [],
    EffectsModule.forRoot([]),
    StoreRouterConnectingModule.forRoot({
      stateKey: 'router',
    }),
    NorthstarModule.forRoot({
      appName: 'polaris-home',
      appVersion: '0.1.0',
      oidcSettings: environment.oidcSettings,
      logSettings: {
        interval: 10000,
      },
      devModules: environment.loadLocalDevModules ? devModules : [],
    }),
    NorthstarAlertsModule,
    FooterModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  providers: [
    appInitializer,
    DashboardService,
    { provide: COMPILER_OPTIONS, useValue: [{ useJit: true }] },
    {
      provide: CompilerFactory,
      useClass: JitCompilerFactory,
      deps: [COMPILER_OPTIONS],
    },
    { provide: Compiler, useFactory: createCompiler, deps: [CompilerFactory] },
    { provide: ENVIRONMENT_CONFIG, useFactory: envFactory },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    public appRef: ApplicationRef,
    private readonly appStore: Store<Record<string, unknown>>
  ) {}

  hmrOnInit(store: StoreType) {
    if (!store || !store.rootState) {
      return;
    }
    if (store.rootState) {
      // Important! Don't HMR the router state since we need to load the dynamic routes before nav starts.
      delete store.rootState.router;

      this.appStore.dispatch({
        type: 'SET_ROOT_STATE',
        payload: store.rootState,
      });
    }
    // set input values
    if ('restoreInputValues' in store) {
      const restoreInputValues = store.restoreInputValues;
      setTimeout(restoreInputValues);
    }

    this.appRef.tick();
    Object.keys(store).forEach((prop) => delete store[prop]);
  }
  hmrOnDestroy(store: StoreType) {
    const cmpLocation = this.appRef.components.map(
      (cmp) => cmp.location.nativeElement
    );
    // save state
    this.appStore.pipe(take(1)).subscribe((s) => (store.rootState = s));
    // recreate root elements
    store.disposeOldHosts = createNewHosts(cmpLocation);
    // save input values
    store.restoreInputValues = createInputTransfer();
    // remove styles
    removeNgStyles();
  }
  hmrAfterDestroy(store: StoreType) {
    // display new elements
    store.disposeOldHosts();
    delete store.disposeOldHosts;
  }
}
